import React, {useState} from 'react';
import {Box, Card, Button, Typography, InputAdornment, FormControl, Autocomplete, TextField, debounce, Stack, Link} from '@mui/material/';
import SearchIcon from '@mui/icons-material/Search';
import Grid from '@mui/material/Grid2';
import ShortFiler from '../../../../types/filers/shortFiler';

const FollowTheMoney = (): JSX.Element => {

  const [contributionOpen, setContributionOpen] = useState(false);
  const [expenditureOpen, setExpenditureOpen] = useState(false);
  const [filerOptions, setFilerOptions] = useState<readonly ShortFiler[]>([]);
  const [selectedFiler, setSelectedFiler] = useState<ShortFiler>();

  const [showContributionError, setShowContributionError] = useState(false);
  const [contributionErrorMessage, setContributionErrorMessage] = useState("");
  const [showExpenditureError, setShowExpenditureError] = useState(false);
  const [expenditureErrorMessage, setExpenditureErrorMessage] = useState("");

  const handleContributionOpen = () => {
    setContributionOpen(true);
  };

  const handleContributionClose = () => {
    setContributionOpen(false);
    setFilerOptions([]);
  };

  const handleExpenditureOpen = () => {
    setExpenditureOpen(true);
  };

  const handleExpenditureClose = () => {
    setExpenditureOpen(false);
    setFilerOptions([]);
  };

  const filterByName = debounce((event: any) => {
    let searchInput = event.target.value.toLowerCase();
    (async () => {
      refreshFilerList(searchInput);
    })();
  }, 800);


  const updateSelectedFiler = (filer: ShortFiler) => {
    setSelectedFiler(filer);
  }

  const clearSelectedFiler = () => {
    setSelectedFiler(undefined);
  }


  const refreshFilerList = async (searchInput: string) => {

    setShowContributionError(false);
    setContributionErrorMessage("");
    setShowExpenditureError(false);
    setExpenditureErrorMessage("");

    try{
      if(searchInput){
        let url = `${process.env.REACT_APP_API_BASE_URL}/filers/listings/A?searchPhrase=${searchInput}`;
        await fetch(url)
        .then(result => result.json())
        .then(result => {
          setFilerOptions(result);
        });
      }
      else{
        setFilerOptions([]);
      }

    }catch(error){
      console.error('Uncaught Error:', error);
    }
  }

  const searchFilerContributions = () => {
    if(selectedFiler && selectedFiler.cpfId){
      window.location.href = `/filers?q=${selectedFiler.cpfId}&section=Contributions`;
    }
    else{
      setShowContributionError(true);
      setContributionErrorMessage("Please select a filer to search.");
    }
  }

  const searchFilerExpenditures = () => {
    if(selectedFiler && selectedFiler.cpfId){
      window.location.href = `/filers?q=${selectedFiler.cpfId}&section=Expenditures`;
    }
    else{
      setShowExpenditureError(true);
      setExpenditureErrorMessage("Please select a filer to search.");
    }
  }

  return (
    <Box marginTop={5} marginBottom={4}>
      <Box marginBottom={4}>
        <Typography
          variant="h4"
          data-aos={'fade-up'}
          align={'center'}
          fontWeight={700}
          gutterBottom
        >
          Follow the Money
        </Typography>
        <Typography
          variant="h6"
          color={'text.secondary'}
          align={'center'}
          data-aos={'fade-up'}>
          Search data for candidates and committees. Track every dollar raised and spent in Massachusetts elections.
        </Typography>
      </Box>
      <Grid container spacing={4}>
        <Grid size={{xs:12, md:6}}>
          <Box
            padding={{ xs: 3, sm: 6 }}
            width={1}
            component={Card}
            boxShadow={4}
            data-aos="fade-up"
          >
            <Box
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              flexDirection={{ xs: 'column', sm: 'row' }}
            >
              <Box>
                <Typography fontWeight={700} variant={'h6'} gutterBottom paddingBottom={0.5}>
                    Contributions Received
                </Typography>
                <FormControl variant='standard'>
                  <Autocomplete
                    filterOptions={(x) => x}
                    sx={{ width: 320 }}
                    onChange={(event: any, newValue: ShortFiler | null) => {
                      if (newValue) {
                          updateSelectedFiler(newValue);
                      }
                      else{
                          clearSelectedFiler();
                      }
                    }}
                    open={contributionOpen}
                    onOpen={handleContributionOpen}
                    onClose={handleContributionClose}
                    isOptionEqualToValue={(option, value) => option.cpfId === value.cpfId}
                    getOptionLabel={(option) => option.filerName}
                    renderOption={(props, option) => 
                      (
                        <Stack 
                          key={option.cpfId}
                          direction="row" 
                          display={"flex"} 
                          justifyContent={"flex-start"}
                        >
                          <Stack>
                            {/*placeholder for filer photos*/}                        
                          </Stack>
                          <Stack>
                              <Typography variant='body2' {...props}>
                                {option.cpfId} {option.filerName}
                              </Typography>
                              {
                                option.isCandidate && 
                                <Typography variant='body2'  component={"i"} color='text.secondary' paddingLeft={2}>
                                  {option.officeSought}
                                </Typography>
                              }
                          </Stack>
                        </Stack>
                        )
                    }
                    options={filerOptions}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='standard'
                        placeholder={"Type filer name or ID..."}
                        onChange={filterByName}
                        error={showContributionError}
                        helperText={contributionErrorMessage}
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon color="primary" />
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Box>
              <Box display="flex" marginTop={{ xs: 2, md: 0 }}>
                <Button onClick={searchFilerContributions} variant="contained" color="primary" size="large">
                  Search
                </Button>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid size={{xs:12, md:6}}>
          <Box
            padding={{ xs: 3, sm: 6 }}
            width={1}
            component={Card}
            boxShadow={4}
            data-aos="fade-up"
          >
            <Box
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              flexDirection={{ xs: 'column', sm: 'row' }}
            >
              <Box>
                <Typography fontWeight={700} variant={'h6'} gutterBottom>
                    Expenditures Made
                </Typography>
                <FormControl variant='standard'>
                  <Autocomplete
                    filterOptions={(x) => x}
                    sx={{ width: 320 }}
                    onChange={(event: any, newValue: ShortFiler | null) => {
                      if (newValue) {
                          updateSelectedFiler(newValue);
                      }
                      else{
                          clearSelectedFiler();
                      }
                    }}
                    open={expenditureOpen}
                    onOpen={handleExpenditureOpen}
                    onClose={handleExpenditureClose}
                    isOptionEqualToValue={(option, value) => option.cpfId === value.cpfId}
                    getOptionLabel={(option) => option.filerName}
                    renderOption={(props, option) => 
                      (
                        <Stack 
                          key={option.cpfId}
                          direction="row" 
                          display={"flex"} 
                          justifyContent={"flex-start"}
                        >
                          <Stack>
                            {/*placeholder for filer photos*/}                        
                          </Stack>
                          <Stack>
                              <Typography variant='body2' {...props}>
                                {option.cpfId} {option.filerName}
                              </Typography>
                              {
                                option.isCandidate && 
                                <Typography variant='body2'  component={"i"} color='text.secondary' paddingLeft={2}>
                                  {option.officeSought}
                                </Typography>
                              }
                          </Stack>
                        </Stack>
                        )
                    }
                    options={filerOptions}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='standard'
                        placeholder={"Type filer name or ID..."}
                        onChange={filterByName}
                        error={showExpenditureError}
                        helperText={expenditureErrorMessage}
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon color="primary" />
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Box>
              <Box display="flex" marginTop={{ xs: 2, md: 0 }}>
                <Button onClick={searchFilerExpenditures} variant="contained" color="secondary" size="large">
                  Search
                </Button>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FollowTheMoney;

